import { useContext } from 'react';
import { Box } from '../Common/Box';
import { OtherPaymentMethod } from '../PaymentMethodsBoxes/Other';
import { ApplePayCard } from './ApplePayCard';
import { CheckoutContext } from '@/context/CheckoutContext';
import { useTranslate } from '@/hooks/useTranslate';
import type { AvailablePaymentMethod } from '@/types/checkout';
import { PaymentMethodEnum, type ExcludeOtherPaymentMethod } from '@/types/common';
import {
  getPaymentMethodLogoUrl,
  getPaymentMethodTestId,
  isCardPaymentMethod,
  shouldUseBeDisplayName,
} from '@/utils/paymentMethods';
import { trackAndNotifyPaymentMethodSelected } from '@/utils/paymentClickEvents';

interface PaymentMethodProps {
  method: AvailablePaymentMethod;
}

export const PaymentMethod = ({ method }: PaymentMethodProps) => {
  const context = useContext(CheckoutContext);
  const {
    paymentMethodSelected,
    actions: { setPaymentMethodSelected },
  } = context;
  const { t } = useTranslate();

  const name = method.paymentMethodName;

  const handleSelect = () => {
    trackAndNotifyPaymentMethodSelected(context, name as PaymentMethodEnum);
    setPaymentMethodSelected(name as ExcludeOtherPaymentMethod);
  };

  if (name === PaymentMethodEnum.OTHER) {
    return (
      <div data-testid={getPaymentMethodTestId(name)} className="w-full">
        <OtherPaymentMethod />
      </div>
    );
  }

  return (
    <div data-testid={getPaymentMethodTestId(name)} className="w-full">
      <Box isSelected={paymentMethodSelected === name} onSelect={handleSelect}>
        {isCardPaymentMethod(name) ? (
          <div className="flex flex-row items-center gap-1">
            <img className="w-4" src={getPaymentMethodLogoUrl(name)} alt={method.paymentMethodDisplayName} />
            <div className="text-sm color-base-foreground">
              {shouldUseBeDisplayName(name) ? method.paymentMethodDisplayName : t('creditCard')}
            </div>
          </div>
        ) : name === PaymentMethodEnum.APPLE_PAY ? (
          <ApplePayCard />
        ) : shouldUseBeDisplayName(name) ? (
          // No CDN logo for this method — the name IS the box (see the predicate).
          <div className="text-sm color-base-foreground">{method.paymentMethodDisplayName}</div>
        ) : (
          <img src={getPaymentMethodLogoUrl(name)} alt={method.paymentMethodDisplayName} />
        )}
      </Box>
    </div>
  );
};
